import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useTheme } from '../../context/ThemeContext';
import { Button } from '../ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { 
    Menu, 
    ShoppingCart, 
    User, 
    LogOut, 
    Package, 
    Cpu,
    Monitor,
    HardDrive,
    Gamepad2,
    Briefcase,
    Wrench,
    Settings,
    Sun,
    Moon,
    MonitorSmartphone,
    Smartphone,
    Headphones,
    UserCircle,
    ChevronDown,
    Laptop,
    CircuitBoard,
    MemoryStick,
    Database,
    Zap,
    Box,
    Fan,
    Heart,
    MessageSquare
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { NotificationBell } from './NotificationBell';

// PC categories dropdown items
const pcCategories = [
    { href: '/products/gamer-pc', label: 'Gamer PC-k', icon: Gamepad2 },
    { href: '/products/irodai-pc', label: 'Irodai PC-k', icon: Briefcase },
    { href: '/products/munkaallomas', label: 'Munkaállomások', icon: Monitor },
    { href: '/products/laptopok', label: 'Laptopok', icon: Laptop },
];

// Component categories dropdown items
const componentCategories = [
    { href: '/products/gpu', label: 'Videokártyák', icon: Monitor },
    { href: '/products/cpu', label: 'Processzorok', icon: Cpu },
    { href: '/products/alaplap', label: 'Alaplapok', icon: CircuitBoard },
    { href: '/products/memoria', label: 'Memóriák', icon: MemoryStick },
    { href: '/products/tarhely', label: 'Tárhelyek (SSD/HDD)', icon: Database },
    { href: '/products/tapegyseg', label: 'Tápegységek', icon: Zap },
    { href: '/products/haz', label: 'Számítógépházak', icon: Box },
    { href: '/products/hutes', label: 'Hűtés', icon: Fan },
    { href: '/products/alkatreszek', label: 'Összes alkatrész', icon: Package },
];

// Other nav links (not in dropdown)
const accessoryCategories = [
    { href: '/products/monitorok', label: 'Monitorok', icon: Monitor },
    { href: '/products/billentyuzetek', label: 'Billentyűzetek', icon: Box },
    { href: '/products/egerek', label: 'Egerek', icon: Box },
    { href: '/products/kabelek', label: 'Kábelek', icon: Box },
    { href: '/products/fejhallgatok', label: 'Fejhallgatók', icon: Headphones },
    { href: '/products/tartozekok', label: 'Összes tartozék', icon: Package },
];

const navLinks = [
    { href: '/products/mobiltelefon', label: 'Mobiltelefonok', icon: Smartphone },
    { href: '/pc-builder', label: 'PC összeállító', icon: Wrench },
];

const themeLabels = {
    light: 'Világos',
    dark: 'Sötét',
    system: 'Rendszer'
};

export function Navbar() {
    const { user, isAuthenticated, isAdmin, logout } = useAuth();
    const { itemCount } = useCart();
    const { theme, toggleTheme, setLightTheme, setDarkTheme, setSystemTheme, isDark } = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    // Track scroll for navbar background
    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    const handleLogoClick = (e) => {
        if (location.pathname === '/') {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // Check if nav link is active
    const isLinkActive = (linkHref) => {
        const [linkPath, linkQuery] = linkHref.split('?');
        if (linkPath !== location.pathname) return false;
        if (!linkQuery) return true;
        const params = new URLSearchParams(location.search);
        const linkParams = new URLSearchParams(linkQuery);
        for (const [key, value] of linkParams) {
            if (params.get(key) !== value) return false;
        }
        return true;
    };

    // Check if any PC category is active
    const isPcCategoryActive = pcCategories.some(cat => isLinkActive(cat.href));
    
    // Check if any component category is active
    const isComponentCategoryActive = componentCategories.some(cat => isLinkActive(cat.href)) || 
        (location.pathname === '/products' && location.search.includes('category=component'));

    const isAccessoryCategoryActive = accessoryCategories.some(cat => isLinkActive(cat.href)) ||
        (location.pathname === '/products' && location.search.includes('category=accessory'));

    return (
        <header className={cn(
            "sticky top-0 z-50 w-full border-b transition-all duration-300",
            scrolled 
                ? "border-border bg-background/95 backdrop-blur-xl shadow-sm" 
                : "border-border/40 bg-background/90 backdrop-blur-lg"
        )}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex h-20 items-center justify-between">
                    <Link to="/" onClick={handleLogoClick} className="flex items-center">
                        <img src={isDark ? "/logo-dark.svg" : "/logo.svg"} alt="NexusRig" className="h-24 w-auto" />
                    </Link>

                    <nav className="hidden lg:flex items-center gap-1">
                        {/* PC Categories Dropdown */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    className={cn(
                                        "px-4 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer flex items-center gap-1",
                                        "hover:bg-secondary hover:text-foreground",
                                        isPcCategoryActive
                                            ? "bg-primary/10 text-primary border border-primary/20"
                                            : "text-muted-foreground"
                                    )}
                                >
                                    <Laptop className="w-4 h-4 mr-1" />
                                    Számítógépek
                                    <ChevronDown className="w-4 h-4" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-48">
                                {pcCategories.map((cat) => (
                                    <DropdownMenuItem 
                                        key={cat.href}
                                        onClick={() => navigate(cat.href)}
                                        className={cn(
                                            "cursor-pointer",
                                            isLinkActive(cat.href) && "bg-primary/10 text-primary"
                                        )}
                                    >
                                        <cat.icon className="mr-2 h-4 w-4" />
                                        {cat.label}
                                        {isLinkActive(cat.href) && <span className="ml-auto text-primary">✓</span>}
                                    </DropdownMenuItem>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>

                        {/* Component Categories Dropdown */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    className={cn(
                                        "px-4 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer flex items-center gap-1",
                                        "hover:bg-secondary hover:text-foreground",
                                        isComponentCategoryActive
                                            ? "bg-primary/10 text-primary border border-primary/20"
                                            : "text-muted-foreground"
                                    )}
                                >
                                    <Cpu className="w-4 h-4 mr-1" />
                                    Alkatrészek
                                    <ChevronDown className="w-4 h-4" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-56">
                                {componentCategories.map((cat, idx) => (
                                    <React.Fragment key={cat.href}>
                                        {idx === componentCategories.length - 1 && <DropdownMenuSeparator />}
                                        <DropdownMenuItem 
                                            onClick={() => navigate(cat.href)}
                                            className={cn(
                                                "cursor-pointer",
                                                isLinkActive(cat.href) && "bg-primary/10 text-primary"
                                            )}
                                        >
                                            <cat.icon className="mr-2 h-4 w-4" />
                                            {cat.label}
                                            {isLinkActive(cat.href) && <span className="ml-auto text-primary">✓</span>}
                                        </DropdownMenuItem>
                                    </React.Fragment>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>

                        {/* Accessory Categories Dropdown */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <button
                                    className={cn(
                                        "px-4 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer flex items-center gap-1",
                                        "hover:bg-secondary hover:text-foreground",
                                        isAccessoryCategoryActive
                                            ? "bg-primary/10 text-primary border border-primary/20"
                                            : "text-muted-foreground"
                                    )}
                                >
                                    <Headphones className="w-4 h-4 mr-1" />
                                    Tartozékok
                                    <ChevronDown className="w-4 h-4" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-48">
                                {accessoryCategories.map((cat, idx) => (
                                    <React.Fragment key={cat.href}>
                                        {idx === accessoryCategories.length - 1 && <DropdownMenuSeparator />}
                                        <DropdownMenuItem 
                                            onClick={() => navigate(cat.href)}
                                            className={cn(
                                                "cursor-pointer",
                                                isLinkActive(cat.href) && "bg-primary/10 text-primary"
                                            )}
                                        >
                                            <cat.icon className="mr-2 h-4 w-4" />
                                            {cat.label}
                                            {isLinkActive(cat.href) && <span className="ml-auto text-primary">✓</span>}
                                        </DropdownMenuItem>
                                    </React.Fragment>
                                ))}
                            </DropdownMenuContent>
                        </DropdownMenu>

                        {/* Other Nav Links */}
                        {navLinks.map((link) => (
                            <button
                                key={link.href}
                                onClick={() => navigate(link.href)}
                                className={cn(
                                    "px-4 py-2 text-sm font-medium rounded-md transition-colors cursor-pointer",
                                    "hover:bg-secondary hover:text-foreground",
                                    isLinkActive(link.href)
                                        ? "bg-primary/10 text-primary border border-primary/20"
                                        : "text-muted-foreground"
                                )}
                            >
                                {link.label}
                            </button>
                        ))}
                    </nav>

                    <div className="flex items-center gap-2">
                        {/* Theme Toggle */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="gap-2" data-testid="theme-toggle">
                                    {isDark ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
                                    <span className="hidden sm:inline text-xs">{themeLabels[theme]}</span>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={setLightTheme} className={theme === 'light' ? 'bg-primary/10' : ''}>
                                    <Sun className="mr-2 h-4 w-4" />
                                    Világos
                                    {theme === 'light' && <span className="ml-auto text-primary">✓</span>}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={setDarkTheme} className={theme === 'dark' ? 'bg-primary/10' : ''}>
                                    <Moon className="mr-2 h-4 w-4" />
                                    Sötét
                                    {theme === 'dark' && <span className="ml-auto text-primary">✓</span>}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={setSystemTheme} className={theme === 'system' ? 'bg-primary/10' : ''}>
                                    <MonitorSmartphone className="mr-2 h-4 w-4" />
                                    Rendszer
                                    {theme === 'system' && <span className="ml-auto text-primary">✓</span>}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>

                        {isAuthenticated && <NotificationBell />}

                        <Button
                            variant="ghost"
                            size="sm"
                            className="relative gap-2"
                            onClick={() => navigate('/cart')}
                            data-testid="cart-button"
                        >
                            <ShoppingCart className="h-4 w-4" />
                            <span className="hidden sm:inline text-xs">
                                {itemCount > 0 ? `${itemCount} termék` : 'Kosár'}
                            </span>
                            {itemCount > 0 && (
                                <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-primary text-[10px] font-bold flex items-center justify-center text-primary-foreground sm:hidden">
                                    {itemCount > 99 ? '99+' : itemCount}
                                </span>
                            )}
                        </Button>

                        {isAuthenticated ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" data-testid="user-menu-button">
                                        <User className="h-5 w-5" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                    <div className="px-2 py-1.5">
                                        <p className="text-sm font-medium">{user?.name}</p>
                                        <p className="text-xs text-muted-foreground">{user?.email}</p>
                                        {isAdmin && (
                                            <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-primary/20 text-primary rounded">
                                                Admin
                                            </span>
                                        )}
                                    </div>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                                        <UserCircle className="mr-2 h-4 w-4" />
                                        Profilom
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => navigate('/dashboard?tab=orders')}>
                                        <Package className="mr-2 h-4 w-4" />
                                        Rendeléseim
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => navigate('/dashboard?tab=builds')}>
                                        <HardDrive className="mr-2 h-4 w-4" />
                                        Összeállításaim
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => navigate('/dashboard?tab=saved')}>
                                        <Heart className="mr-2 h-4 w-4" />
                                        Mentett termékek
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => navigate('/dashboard?tab=support')} data-testid="nav-support-link">
                                        <MessageSquare className="mr-2 h-4 w-4" />
                                        Támogatás
                                    </DropdownMenuItem>
                                    {isAdmin && (
                                        <DropdownMenuItem onClick={() => navigate('/admin')}>
                                            <Settings className="mr-2 h-4 w-4" />
                                            Admin Panel
                                        </DropdownMenuItem>
                                    )}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                                        <LogOut className="mr-2 h-4 w-4" />
                                        Kijelentkezés
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <Button
                                variant="default"
                                size="sm"
                                onClick={() => navigate('/auth')}
                                className="btn-glow"
                                data-testid="login-button"
                            >
                                Bejelentkezés
                            </Button>
                        )}

                        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                            <SheetTrigger asChild>
                                <Button variant="ghost" size="icon" className="lg:hidden">
                                    <Menu className="h-5 w-5" />
                                </Button>
                            </SheetTrigger>
                            <SheetContent side="right" className="w-72">
                                <nav className="flex flex-col gap-2 mt-8">
                                    {/* PC Categories Section */}
                                    <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                        Számítógépek
                                    </div>
                                    {pcCategories.map((link) => {
                                        const Icon = link.icon;
                                        return (
                                            <Link
                                                key={link.href}
                                                to={link.href}
                                                onClick={() => setMobileOpen(false)}
                                                className={cn(
                                                    "flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ml-2",
                                                    "hover:bg-secondary",
                                                    isLinkActive(link.href)
                                                        ? "bg-secondary text-foreground"
                                                        : "text-muted-foreground"
                                                )}
                                            >
                                                <Icon className="h-5 w-5" />
                                                {link.label}
                                            </Link>
                                        );
                                    })}
                                    
                                    <div className="border-t border-border my-2" />
                                    
                                    {/* Component Categories Section */}
                                    <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                        Alkatrészek
                                    </div>
                                    {componentCategories.map((link) => {
                                        const Icon = link.icon;
                                        return (
                                            <Link
                                                key={link.href}
                                                to={link.href}
                                                onClick={() => setMobileOpen(false)}
                                                className={cn(
                                                    "flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ml-2",
                                                    "hover:bg-secondary",
                                                    isLinkActive(link.href)
                                                        ? "bg-secondary text-foreground"
                                                        : "text-muted-foreground"
                                                )}
                                            >
                                                <Icon className="h-5 w-5" />
                                                {link.label}
                                            </Link>
                                        );
                                    })}
                                    
                                    <div className="border-t border-border my-2" />
                                    
                                    {/* Accessory Categories Section */}
                                    <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                        Tartozékok
                                    </div>
                                    {accessoryCategories.map((link) => {
                                        const Icon = link.icon;
                                        return (
                                            <Link
                                                key={link.href}
                                                to={link.href}
                                                onClick={() => setMobileOpen(false)}
                                                className={cn(
                                                    "flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ml-2",
                                                    "hover:bg-secondary",
                                                    isLinkActive(link.href)
                                                        ? "bg-secondary text-foreground"
                                                        : "text-muted-foreground"
                                                )}
                                            >
                                                <Icon className="h-5 w-5" />
                                                {link.label}
                                            </Link>
                                        );
                                    })}
                                    
                                    <div className="border-t border-border my-2" />
                                    
                                    {/* Other Links */}
                                    {navLinks.map((link) => {
                                        const Icon = link.icon;
                                        return (
                                            <Link
                                                key={link.href}
                                                to={link.href}
                                                onClick={() => setMobileOpen(false)}
                                                className={cn(
                                                    "flex items-center gap-3 px-4 py-3 rounded-lg transition-colors",
                                                    "hover:bg-secondary",
                                                    isLinkActive(link.href)
                                                        ? "bg-secondary text-foreground"
                                                        : "text-muted-foreground"
                                                )}
                                            >
                                                <Icon className="h-5 w-5" />
                                                {link.label}
                                            </Link>
                                        );
                                    })}
                                    {isAdmin && (
                                        <Link
                                            to="/admin"
                                            onClick={() => setMobileOpen(false)}
                                            className="flex items-center gap-3 px-4 py-3 rounded-lg transition-colors hover:bg-secondary text-muted-foreground"
                                        >
                                            <Settings className="h-5 w-5" />
                                            Admin Panel
                                        </Link>
                                    )}
                                </nav>
                            </SheetContent>
                        </Sheet>
                    </div>
                </div>
            </div>
        </header>
    );
}

export default Navbar;
